import {useCallback, useId, useMemo} from "react";
import type {ActionMeta, GroupBase, OnChangeValue, Props as ReactSelectProps} from "react-select";
import ReactSelect from "react-select";

import {useSelectTheme} from "../hooks/use_select_theme";
import {IOption} from "../types/IOption";
import {getSelectValue} from "../utils/get_select_value";
import {isMultiOnChangeValue} from "../utils/is_multi_on_change_value";
import {NoOptionsMessage} from "./NoOptionsMessage";

export interface IProps<Value, Option extends IOption<Value>, IsMulti extends boolean, Group extends GroupBase<Option> = GroupBase<Option>>
    extends Omit<ReactSelectProps<Option, IsMulti, Group>, "value" | "onChange"> {
    isBorderless?: boolean;
    onChange: (value: Value | Value[] | null, actionMeta: ActionMeta<Option>) => void;
    value: Value | Value[];
}

export const Select = <Value, Option extends IOption<Value>, IsMulti extends boolean, Group extends GroupBase<Option> = GroupBase<Option>>(
    props: IProps<Value, Option, IsMulti, Group>
) => {
    const {isSearchable, isClearable, placeholder, value, ...selectProps} = props;

    const instanceId = useId();

    // `styles` and `theme` can be overwritten by `Select` props
    const {getTheme, styles} = useSelectTheme<Value, Option, IsMulti, Group>();

    const selectValue = useMemo(() => {
        return getSelectValue<Value, Option, IsMulti, Group>(value, selectProps.options || [], selectProps.isMulti);
    }, [props.value]);

    const selectDefaultValue = useMemo(() => {
        return getSelectValue<Value, Option, IsMulti, Group>(value, selectProps.options || [], selectProps.isMulti);
    }, []);

    const onChange = useCallback(
        (newValue: OnChangeValue<Option, IsMulti>, actionMeta: ActionMeta<Option>) => {
            if (isMultiOnChangeValue<Value, Option, IsMulti>(newValue, selectProps.isMulti)) {
                props.onChange(
                    newValue.map(({value}) => value),
                    actionMeta
                );
            } else {
                // Must be `null` for empty value in order to clear form value
                props.onChange(newValue?.value ?? null, actionMeta);
            }
        },
        [props.onChange]
    );

    return (
        <ReactSelect<Option, IsMulti, Group>
            instanceId={instanceId}
            isSearchable={isSearchable || false}
            isClearable={isClearable || false}
            styles={styles}
            theme={getTheme}
            {...selectProps}
            defaultValue={selectDefaultValue}
            noOptionsMessage={({inputValue}) => <NoOptionsMessage inputValue={inputValue} isMulti={selectProps.isMulti} />}
            onChange={onChange}
            placeholder={placeholder || "Wybierz"}
            value={selectValue}
        />
    );
};
